<script setup lang="ts">
import { loadUnicornStudio } from '@/lib/unicornStudio';

type LoadingQuality = 'high' | 'balanced';

const SCENE_PRESETS = {
  /** 与 poster 画板 loading 一致：首屏 / 第二屏主画布 */
  high: { fps: 60, dpi: 1, lazyLoad: false },
  /** 第三屏双 loading 并发时使用，降低 GPU 压力 */
  balanced: { fps: 30, dpi: 1, lazyLoad: true },
} as const;

interface Props {
  /** 与画板 Frame loading 一致：#1D1D1B */
  background?: string;
  /** high：完整 Unicorn 动效；balanced：并发 loading 时降配 */
  quality?: LoadingQuality;
}

const props = withDefaults(defineProps<Props>(), {
  background: '#1D1D1B',
  quality: 'balanced',
});

const rootRef = ref<HTMLElement | null>(null);
const usHostRef = ref<HTMLElement | null>(null);

let scene: { resize?: () => void; destroy?: () => void } | null = null;
let resizeObserver: ResizeObserver | null = null;
const hostId = `seede-loading-${Math.random().toString(36).slice(2, 9)}`;

const initScene = async () => {
  if (!usHostRef.value) return;

  usHostRef.value.id = hostId;

  try {
    const US = await loadUnicornStudio();
    if (!usHostRef.value) return;

    const preset = SCENE_PRESETS[props.quality];

    scene = await US.addScene({
      elementId: hostId,
      fps: preset.fps,
      scale: 1,
      dpi: preset.dpi,
      filePath: '/unicornstudio_animation_remix.json',
      lazyLoad: preset.lazyLoad,
      fixed: false,
      production: false,
      interactivity: {
        mouse: { disabled: true, disableMobile: true },
      },
      altText: 'Loading',
      ariaLabel: 'Loading animation',
    });

    try {
      scene?.resize?.();
    } catch {
      /* ignore */
    }
  } catch (e) {
    console.warn('[SeedeFrameLoading] Unicorn scene failed', e);
  }
};

const destroyScene = () => {
  try {
    scene?.destroy?.();
  } catch {
    /* ignore */
  }
  scene = null;
};

onMounted(() => {
  void initScene();

  if (rootRef.value) {
    resizeObserver = new ResizeObserver(() => {
      try {
        scene?.resize?.();
      } catch {
        /* ignore */
      }
    });
    resizeObserver.observe(rootRef.value);
  }
});

onBeforeUnmount(() => {
  resizeObserver?.disconnect();
  resizeObserver = null;
  destroyScene();
});
</script>

<template>
  <div
    ref="rootRef"
    class="seede-frame-loading pointer-events-none absolute inset-0 overflow-hidden"
    :style="{ backgroundColor: props.background }"
    aria-hidden="true"
  >
    <div ref="usHostRef" class="seede-frame-loading__us absolute inset-0" />
    <img class="seede-frame-loading__logo" src="/Seede_logotype.svg" alt="" draggable="false" />
  </div>
</template>

<style scoped>
/* 与 apps/poster/src/components/stage/Render.vue 画板生图 loading 一致 */
.seede-frame-loading__us {
  z-index: 1;
}

.seede-frame-loading__logo {
  position: absolute;
  left: 50%;
  top: 50%;
  z-index: 2;
  width: clamp(28px, 18%, 56px);
  height: auto;
  object-fit: contain;
  pointer-events: none;
  user-select: none;
  -webkit-user-drag: none;
  transform: translate(-50%, -50%);
  filter: brightness(0) invert(1) !important;
  animation: seedeFrameLoadingLogoPulse 1.2s ease-in-out infinite;
}

@keyframes seedeFrameLoadingLogoPulse {
  0%,
  100% {
    opacity: 0.2;
  }

  50% {
    opacity: 0.55;
  }
}
</style>
